import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {useQueryClient} from '@tanstack/react-query';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {array, bool, func, object} from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import AreaIndicator from '../common/AreaIndicator.jsx';
import FeaturesGrid from './FeaturesGrid.jsx';
import MinimumCostAlert from './MinimumCostAlert.jsx';
import QuotaDisplay from './QuotaDisplay.jsx';
import {getMinimumSQMCost} from './util.js';
import {notify} from '@actions/notifications';
import {useMakeQuotaReservations} from '@api/quotaReservations';
import {trackEvent} from '@app/analytics';
import {
  queryKeys as featuresQueryKeys,
  useWithinBoundary,
} from '@app/api/features';
import {useGetEstimates} from '@app/api/quotaEstimates';
import {formatProductTermRange} from '@app/util/format-dates';
import {getHashFromId} from '@app/util/granero.js';
import ErrorMessage from '@components/common/ErrorMessage';

const ProductCard = ({
  product,
  features,
  loading,
  selectedFeatureRefs,
  setSelectedFeatureRefs,
  onZoomToFeature,
  onReserve,
  collection,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {fmUseCollectionRef} = useFlags();

  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  const productAccessibilityZone = useMemo(
    () => product?.accessibility_zone?.[0],
    [product]
  );

  const withinBoundaryQuery = useWithinBoundary(
    {
      boundary: productAccessibilityZone,
      ref: collection['pl:ref'],
    },
    {
      enabled: !!productAccessibilityZone,
    }
  );

  const {hashIdsOutsideBoundary} = withinBoundaryQuery.data ?? {};

  const featuresToReserve = useMemo(
    () =>
      features.filter(
        feature =>
          !feature.relations?.find(
            relation =>
              relation.system === 'admin' &&
              relation.key === 'pa' &&
              relation.value === product.id.toString()
          )
      ),
    [features, product.id]
  );

  const unreservedFeatureRefs = useMemo(
    () =>
      featuresToReserve
        ?.map(feature => getFeatureReference(feature))
        .filter(ref => !hashIdsOutsideBoundary?.includes(getHashFromId(ref))),
    [featuresToReserve, hashIdsOutsideBoundary]
  );
  // only run this if the features do NOT have a relation on it already
  const {
    data: estimatesById,
    isFetching: fetchingEstimates,
    isLoading: loadingEstimates,
    isError: estimatesError,
    refetch: refetchEstimates,
  } = useGetEstimates(
    {
      aoi_refs: unreservedFeatureRefs,
      product_access_id: product.id,
    },
    {
      enabled:
        unreservedFeatureRefs?.length > 0 && !withinBoundaryQuery.isLoading,
    }
  );

  const selectionsCost = useMemo(() => {
    let cost = 0;
    selectedFeatureRefs.forEach(featureRef => {
      cost += estimatesById[featureRef]?.cost || 0;
    });

    return cost;
  }, [estimatesById, selectedFeatureRefs]);

  const remainingQuota = useMemo(
    () => product.quota_total - product.quota_used - selectionsCost,
    [product.quota_total, product.quota_used, selectionsCost]
  );

  const hasRemainingQuota = product.unlimited_quota || remainingQuota > 0;
  const onRowClick = newModel => {
    const allIds = [...newModel.ids];
    setSelectedFeatureRefs(allIds);
  };

  const {mutate: makeReservations, isPending: isReserving} =
    useMakeQuotaReservations({
      onSuccess: data => {
        const hasSuccess = data.filter(d => !d.error).length > 0;
        const hasErrors = data.filter(d => !!d.error).length > 0;

        if (hasSuccess) {
          onReserve(hasErrors);

          queryClient.invalidateQueries({queryKey: featuresQueryKeys.lists()});

          trackEvent('Product Card', {
            action: 'reserved quota successfully',
            product,
            featuresLength: features.length,
          });

          dispatch(
            notify({
              severity: 'success',
              message: `Your request is being processed${selectedFeatureRefs.length >= 100 ? ', it may take a few minutes to complete' : ''}. Reservations will show up in the "Reserved" tab.`,
              persist: true,
            })
          );
        }

        if (hasErrors) {
          dispatch(
            notify({
              severity: 'error',
              message: `There was an unexpected error while making ${hasSuccess.length ? 'some of the ' : ''}reservations, please try again.`,
            })
          );
        }
      },
    });

  const onReserveClick = async () => {
    const allFeaturesSelected =
      selectedFeatureRefs.length > 0 &&
      selectedFeatureRefs.length === collection.feature_count;

    // if we have selected all features in a collection, reserve the collection ref instead
    const refsToReserve =
      allFeaturesSelected && !!fmUseCollectionRef
        ? [collection['pl:ref']]
        : selectedFeatureRefs;

    makeReservations({
      productId: product.id,
      refs: refsToReserve,
      collectionId: collection.id,
    });
  };

  useEffect(() => {
    if (!loading && !withinBoundaryQuery.isLoading) {
      const selectedRefs = featuresToReserve
        .map(f => getFeatureReference(f))
        .filter(ref => !hashIdsOutsideBoundary?.includes(getHashFromId(ref)));
      setSelectedFeatureRefs(selectedRefs);
    }
  }, [
    withinBoundaryQuery.isLoading,
    hashIdsOutsideBoundary,
    featuresToReserve,
    loading,
    setSelectedFeatureRefs,
  ]);

  useEffect(() => {
    setFeaturesExpanded(false);
  }, [product]);

  if (!product) {
    return false;
  }

  if (estimatesError) {
    return (
      <ErrorMessage
        onRetry={refetchEstimates}
        statusCode={estimatesError?.response?.status}
        message="There was an error while obtaining quota costs."
      />
    );
  }

  return (
    <>
      <Card
        elevation={18}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardContent>
          <Grid container rowSpacing={1} direction="column">
            <Grid
              container
              direction="row"
              size={12}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid size={8}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: 'inline',
                  }}
                >
                  {product.title}
                  <Tooltip
                    title={`Access dates: ${formatProductTermRange(product)}`}
                    placement="top"
                  >
                    <CalendarIcon
                      color="action"
                      sx={{
                        marginTop: -0.5,
                        verticalAlign: 'middle',
                        marginLeft: 1,
                      }}
                    />
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid sx={{textAlign: 'right'}} size={4}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={
                    loading ||
                    loadingEstimates ||
                    fetchingEstimates ||
                    selectedFeatureRefs.length === 0 ||
                    !hasRemainingQuota ||
                    isReserving ||
                    !!estimatesError
                  }
                  sx={{marginLeft: 'auto'}}
                  onClick={onReserveClick}
                >
                  {isReserving ? 'Reserving...' : 'Reserve quota'}
                </Button>
              </Grid>
            </Grid>
            <Grid container direction="row" size={12}>
              <Grid size={8}>
                <Typography variant="subtitle1">Quota available</Typography>
              </Grid>
              <Grid size={4}>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  <QuotaDisplay
                    quota={product.quota_total - product.quota_used}
                    isUnlimited={product.unlimited_quota}
                  />
                </Typography>
              </Grid>
            </Grid>
            <Grid container direction="row" size={12}>
              <Grid size={8}>
                <Typography>Quota cost</Typography>
              </Grid>
              <Grid size={4}>
                <Typography
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  {loadingEstimates || fetchingEstimates || loading ? (
                    'Loading...'
                  ) : (
                    <AreaIndicator squareMeters={selectionsCost} />
                  )}
                </Typography>
              </Grid>
            </Grid>
            {!product.unlimited_quota && (
              <>
                <Grid container direction="row" sx={{marginY: 0.5}} size={12}>
                  <Grid size={9}></Grid>
                  <Grid size={3}>
                    <Divider flexItem />
                  </Grid>
                </Grid>
                <Grid container direction="row" size={12}>
                  <Grid size={8}>
                    <Typography>Remaining quota</Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography
                      sx={{
                        justifyContent: 'flex-end',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {loading || loadingEstimates || fetchingEstimates ? (
                        'Loading...'
                      ) : (
                        <>
                          {!hasRemainingQuota && (
                            <Tooltip title="Insufficient quota. Please add more or release.">
                              <ErrorIcon
                                color="error"
                                sx={{marginRight: 0.5}}
                              />
                            </Tooltip>
                          )}
                          <AreaIndicator squareMeters={remainingQuota} />
                        </>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container direction="row" sx={{marginTop: 2}} size={12}>
              <Grid sx={{width: '100%'}}>
                <MinimumCostAlert product={product} />
              </Grid>
              <Grid sx={{width: '100%'}}>
                {hashIdsOutsideBoundary?.length > 0 && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      padding: theme => theme.spacing(0.5, 1),
                    }}
                  >
                    There are features outside of the product&apos;s
                    accessibility zone. These features are not included in the
                    estimates or allowed to be selected for reservation.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <Divider sx={{marginTop: 1}} />
        <CardActions sx={{width: '100%', justifyContent: 'space-between'}}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              disabled={loading || loadingEstimates}
              onClick={() => setFeaturesExpanded(prevState => !prevState)}
              sx={{
                transform: !featuresExpanded
                  ? 'rotate(0deg)'
                  : 'rotate(180deg)',
                transition: theme =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
              }}
            >
              {loading || loadingEstimates || fetchingEstimates ? (
                <CircularProgress size={24} />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
            <Typography variant="subtitle1">
              Features{' '}
              {loading || loadingEstimates
                ? ''
                : `(${featuresToReserve.length})`}
            </Typography>
          </Stack>
          {featuresToReserve.length === 0 && !loading && !loadingEstimates && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              All features have been reserved in this collection
            </Typography>
          )}
        </CardActions>
        <Collapse
          in={featuresExpanded}
          timeout="auto"
          mountOnEnter
          unmountOnExit
          sx={{
            '.MuiCollapse-wrapper': {
              // keeps data grid happy and not janky
              height: '100%',
              minHeight: 150,
            },
          }}
        >
          <Box
            sx={{
              height: '100%',
              minHeight: 50,
              maxHeight: 500,
            }}
          >
            <FeaturesGrid
              features={featuresToReserve}
              estimates={estimatesById}
              rowSelectionModel={{
                type: 'include',
                ids: new Set(
                  selectedFeatureRefs?.length ? selectedFeatureRefs : null
                ),
              }}
              onRowSelectionModelChange={onRowClick}
              loading={loading}
              onZoomToClicked={onZoomToFeature}
              minCost={getMinimumSQMCost(product)}
              collection={collection}
              isRowSelectable={params => {
                if (isReserving) {
                  return false;
                }
                const canSelect = !hashIdsOutsideBoundary?.includes(
                  getHashFromId(params.row.properties['pl:ref'])
                );
                return canSelect;
              }}
            />
          </Box>
        </Collapse>
      </Card>
    </>
  );
};

ProductCard.propTypes = {
  features: array,
  product: object,
  loading: bool,
  selectedFeatureRefs: array,
  setSelectedFeatureRefs: func,
  onZoomToFeature: func,
  onReserve: func,
  collection: object,
};

export default ProductCard;
